'use client'

import { useEffect, useSyncExternalStore } from 'react'
import { isNativePlatform } from '@/lib/utils/platform'

/** Pixels from bottom obscured by the on-screen keyboard (native or mobile web). */
const listeners = new Set<() => void>()
let insetPx = 0

/** @internal — exported for unit tests */
export function computeInsetFromViewportHeight(
  vvHeight: number,
  baseline: number
): { inset: number; baseline: number } {
  let b = baseline
  if (vvHeight >= b * 0.92) {
    b = vvHeight
  }
  const inset = Math.max(0, b - vvHeight)
  return { baseline: b, inset }
}

function setInset(px: number) {
  const rounded = Math.max(0, Math.round(px))
  if (rounded === insetPx) return
  insetPx = rounded
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function getSnapshot() {
  return insetPx
}

function getServerSnapshot() {
  return 0
}

/** Synchronous read for layout (visual viewport binding). */
export function getKeyboardInsetPx(): number {
  return insetPx
}

/** Re-run layout when Capacitor / vv store updates (may fire before visualViewport resize). */
export function subscribeKeyboardInsetStore(listener: () => void): () => void {
  return subscribe(listener)
}

let nativeRefCount = 0
let nativeCleanup: (() => void) | null = null
let nativeAttachPromise: Promise<void> | null = null

async function attachNativeKeyboard() {
  if (nativeCleanup) return
  if (nativeAttachPromise) {
    await nativeAttachPromise
    return
  }
  nativeAttachPromise = (async () => {
    try {
      const { Keyboard } = await import('@capacitor/keyboard')
      const show = await Keyboard.addListener(
        'keyboardWillShow',
        (info: { keyboardHeight?: number }) => {
          setInset(info.keyboardHeight ?? 0)
        }
      )
      const hideWill = await Keyboard.addListener('keyboardWillHide', () => setInset(0))
      const hideDid = await Keyboard.addListener('keyboardDidHide', () => setInset(0))
      const cleanup = () => {
        show.remove()
        hideWill.remove()
        hideDid.remove()
        nativeCleanup = null
        setInset(0)
      }
      nativeCleanup = cleanup
      // All subscribers may have unmounted while we awaited the plugin
      if (nativeRefCount <= 0) {
        cleanup()
      }
    } catch {
      nativeCleanup = null
    } finally {
      nativeAttachPromise = null
    }
  })()
  await nativeAttachPromise
}

function detachNativeKeyboard() {
  nativeCleanup?.()
}

let vvRefCount = 0
let vvCleanup: (() => void) | null = null

function attachVisualViewport() {
  if (vvCleanup || typeof window === 'undefined') return
  const vv = window.visualViewport
  if (!vv) return

  let baseline = vv.height

  const update = () => {
    const { baseline: nextBase, inset } = computeInsetFromViewportHeight(vv.height, baseline)
    baseline = nextBase
    setInset(inset)
  }

  update()
  vv.addEventListener('resize', update)
  vv.addEventListener('scroll', update)
  vvCleanup = () => {
    vv.removeEventListener('resize', update)
    vv.removeEventListener('scroll', update)
    vvCleanup = null
    setInset(0)
  }
}

function detachVisualViewport() {
  vvCleanup?.()
}

/**
 * Ensures a single global subscription to keyboard geometry (ref-counted).
 * Call from `useKeyboardInset` only.
 */
function useKeyboardInsetSubscription() {
  useEffect(() => {
    if (isNativePlatform()) {
      nativeRefCount += 1
      void attachNativeKeyboard()
      return () => {
        nativeRefCount -= 1
        if (nativeRefCount <= 0) {
          nativeRefCount = 0
          detachNativeKeyboard()
        }
      }
    }

    if (typeof window !== 'undefined' && 'ontouchstart' in window) {
      vvRefCount += 1
      attachVisualViewport()
      return () => {
        vvRefCount -= 1
        if (vvRefCount <= 0) {
          vvRefCount = 0
          detachVisualViewport()
        }
      }
    }

    return undefined
  }, [])
}

/**
 * Shared keyboard inset for layout (px). On Capacitor uses Keyboard plugin events;
 * on mobile web uses visualViewport with a self-healing baseline.
 */
export function useKeyboardInset(): { insetPx: number; isKeyboardOpen: boolean } {
  useKeyboardInsetSubscription()
  const px = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  return {
    insetPx: px,
    isKeyboardOpen: px >= 64,
  }
}
